import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Award, AlertCircle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BehaviorManagement() {
  const [behaviors, setBehaviors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBehavior, setEditingBehavior] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    type: 'merit',
    points: '',
    description: '',
  });
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    fetchBehaviors();
  }, []);

  const fetchBehaviors = async () => {
    try {
      const response = await axios.get(`${API}/admin/behaviors`, {
        headers: getAuthHeaders(),
      });
      setBehaviors(response.data);
    } catch (error) {
      console.error('Error fetching behaviors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBehavior) {
        await axios.put(
          `${API}/admin/behaviors/${editingBehavior.id}`,
          {
            name: formData.name,
            type: formData.type,
            points: parseInt(formData.points),
            description: formData.description || null,
          },
          { headers: getAuthHeaders() }
        );
        toast.success('Мінез-құлық жаңартылды');
      } else {
        await axios.post(
          `${API}/admin/behaviors`,
          {
            name: formData.name,
            type: formData.type,
            points: parseInt(formData.points),
            description: formData.description || null,
          },
          { headers: getAuthHeaders() }
        );
        toast.success('Мінез-құлық қосылды');
      }
      setDialogOpen(false);
      setEditingBehavior(null);
      setFormData({ name: '', type: 'merit', points: '', description: '' });
      fetchBehaviors();
    } catch (error) {
      toast.error('Қате орын алды');
      console.error('Error saving behavior:', error);
    }
  };

  const handleEdit = (behavior) => {
    setEditingBehavior(behavior);
    setFormData({
      name: behavior.name,
      type: behavior.type,
      points: behavior.points.toString(),
      description: behavior.description || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (behaviorId) => {
    if (!window.confirm('Бұл мінез-құлықты өшіргіңіз келетініне сенімдісіз бе?')) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/behaviors/${behaviorId}`, {
        headers: getAuthHeaders(),
      });
      toast.success('Мінез-құлық өшірілді');
      fetchBehaviors();
    } catch (error) {
      toast.error('Қате орын алды');
      console.error('Error deleting behavior:', error);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingBehavior(null);
    setFormData({ name: '', type: 'merit', points: '', description: '' });
  };

  if (loading) {
    return <div className="text-center py-12">Жүктелуде...</div>;
  }

  // Filter behaviors by search term
  const filteredBehaviors = behaviors.filter((b) =>
    b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (b.description && b.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const meritBehaviors = filteredBehaviors.filter((b) => b.type === 'merit');
  const demeritBehaviors = filteredBehaviors.filter((b) => b.type === 'demerit');

  return (
    <div className="space-y-6 fade-in" data-testid="behavior-management">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold heading-font tracking-tight">
            Мінез-құлық басқаруы
          </h2>
          <p className="text-muted">Марапат және ескерту мінез-құлықтарын басқару</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditingBehavior(null)} data-testid="add-behavior-button">
              <Plus className="mr-2 h-4 w-4" />
              Жаңа мінез-құлық
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingBehavior ? 'Мінез-құлықты өңдеу' : 'Жаңа мінез-құлық қосу'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Атауы</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="behavior-name-input"
                />
              </div>

              <div>
                <Label htmlFor="type">Түрі</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger data-testid="behavior-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="merit">Марапат</SelectItem>
                    <SelectItem value="demerit">Ескерту</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="points">Ұпайлар</Label>
                <Input
                  id="points"
                  type="number"
                  value={formData.points}
                  onChange={(e) => setFormData({ ...formData, points: e.target.value })}
                  required
                  min="1"
                  data-testid="behavior-points-input"
                />
              </div>

              <div>
                <Label htmlFor="description">Сипаттама (міндетті емес)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="behavior-description-input"
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={handleDialogClose}>
                  Болдырмау
                </Button>
                <Button type="submit" data-testid="save-behavior-button">
                  Сақтау
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted h-4 w-4" />
        <Input
          type="text"
          placeholder="Мінез-құлықты іздеу..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 max-w-md"
        />
      </div>

      {/* Merit Behaviors */}
      <div>
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <Award className="w-5 h-5 mr-2 text-green-600" />
          Марапат мінез-құлықтары ({meritBehaviors.length})
        </h3>
        {meritBehaviors.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted">
              Марапат мінез-құлықтары жоқ
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {meritBehaviors.map((behavior) => (
              <Card key={behavior.id} className="paper-card" data-testid={`behavior-${behavior.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg mb-1">{behavior.name}</h4>
                      <span className="merit-badge">+{behavior.points} ұпай</span>
                      {behavior.description && (
                        <p className="text-sm text-muted mt-2">{behavior.description}</p>
                      )}
                    </div>
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(behavior)}
                        data-testid={`edit-behavior-${behavior.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(behavior.id)}
                        data-testid={`delete-behavior-${behavior.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Demerit Behaviors */}
      <div>
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
          Ескерту мінез-құлықтары ({demeritBehaviors.length})
        </h3>
        {demeritBehaviors.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted">
              Ескерту мінез-құлықтары жоқ
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {demeritBehaviors.map((behavior) => (
              <Card key={behavior.id} className="paper-card" data-testid={`behavior-${behavior.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg mb-1">{behavior.name}</h4>
                      <span className="demerit-badge">-{behavior.points} ұпай</span>
                      {behavior.description && (
                        <p className="text-sm text-muted mt-2">{behavior.description}</p>
                      )}
                    </div>
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(behavior)}
                        data-testid={`edit-behavior-${behavior.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(behavior.id)}
                        data-testid={`delete-behavior-${behavior.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}